/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import config from "../config";
import Header from "../components/Header";
import CovidTest from "../assets/SVG/home-icon/covid-test-white2.svg";
import CardioIcon from "../assets/SVG/home-icon/cardio-white.svg";
import "../styles/testselection.css";
import * as action from "../actions";
import { useHistory } from "react-router-dom";
import useTitle from "../hooks/useTitle";

axios.defaults.withCredentials = true;

export default function TestSelection(props) {
  const history = useHistory();
  const dispatch = useDispatch();
  useTitle("Test Selection");

  const [user] = useState(useSelector((state) => state.user));
  const [subscriptions] = useState(useSelector((state) => state.subscriptions));
  const [isLoading, setLoading] = useState(true);
  const [privilege, setPrivilege] = useState({
    covid_access: user.covid_access,
    covid_demo_access: user.covid_demo_access,
    cardio_access: user.cardio_access,
    cardio_poc_access: user.cardio_poc_access,
  });

  useEffect(() => {
    if (!user?.agreement?.privacyPolicy) {
      history.push("/home");
    }
    getUserAccessData();
  }, []);

  const cardioSub = subscriptions?.data?.filter(
    (sub) => sub.productType === "cardio" && (sub.status === "active" || sub.status === "pending_cancel")
  )[0];
  const covidSub = subscriptions?.data?.filter(
    (sub) => sub.productType === "covid" && (sub.status === "active" || sub.status === "pending_cancel")
  )[0];

  const getUserAccessData = () => {
    axios.get(config.backend_url + "/useroperations/getPrivileges").then((response) => {
      // storing user details into redux for using it into the other pages of application
      let updateUser = {
        uid: user.uid,
        firstName: user.firstName,
        lastName: user.lastName,
        last_visit: user.last_visit,
        picture: user.picture,
        loggedIn_time: user.loggedIn_time,
        verified: user.verified,
        ...response.data.privilege,
      };

      dispatch(action.updateUser({ ...user, ...updateUser }));
      setPrivilege(response.data.privilege);
      setLoading(false);
    });
  };

  function handleCovidClick(isDemo) {
    history.push({
      pathname: "/qri",
      state: { covid: true, isDemo },
    });
  }

  const anyAccess =
    privilege.covid_access || privilege.covid_demo_access || privilege.cardio_access || privilege.cardio_poc_access;

  const getTrialAccessByUsage = () => {
    const totalCardioTestCount = user?.userStatus?.totalCardioTestCount;
    const totalCovidTestCount = user?.userStatus?.totalCovidTestCount;
    const covidTrialTestLimit =
      user?.affiliateAccount?.covidTrialTestLimit === undefined
        ? user?.covidTrialTestLimit
        : user?.affiliateAccount?.covidTrialTestLimit;
    const cardioTrialTestLimit =
      user?.affiliateAccount?.cardioTrialTestLimit === undefined
        ? user?.cardioTrialTestLimit
        : user?.affiliateAccount?.cardioTrialTestLimit;
    return { covid: totalCovidTestCount < covidTrialTestLimit, cardio: totalCardioTestCount < cardioTrialTestLimit };
  };

  const trialAccessByUsage = getTrialAccessByUsage();

  const hasCovidAccess = useMemo(() => {
    const accountType = user?.accountType;
    if (!accountType) return false;

    if (accountType === "trial_expired") return false;
    if (accountType === "trial") {
      return (privilege?.covid_access && trialAccessByUsage.covid) ?? false;
    }
    if (accountType === "active") {
      if (user?.hasRevenueCatSub) {
        const totalCovidTestCount = user?.userStatus?.totalCovidTestCount;
        const maxTestCovidByPlan =
          parseInt(user?.userStatus?.activeCovidSub?.usageLimit?.testLimit?.unit) *
          parseInt(user?.userStatus?.activeCovidSub?.usageLimit?.testLimit?.interval_count);
        return (privilege?.covid_access && totalCovidTestCount < maxTestCovidByPlan) ?? false;
      }
      return (privilege?.covid_access && covidSub?.stripe?.latest_invoice?.paid) ?? false;
    }
    return privilege?.covid_access ?? false;
  }, [privilege.covid_access, covidSub?.stripe?.latest_invoice?.paid, user?.accountType, user?.hasRevenueCatSub]);

  const hasCovidDemoAccess = useMemo(() => {
    return privilege?.covid_demo_access ?? false;
  }, [privilege.covid_demo_access]);

  const hasCardioAccess = useMemo(() => {
    const accountType = user?.accountType;
    if (!accountType) return false;
    if (accountType === "trial_expired") return false;
    if (accountType === "trial") {
      return (privilege?.cardio_access && trialAccessByUsage.cardio) ?? false;
    }

    if (accountType === "active") {
      if (user?.hasRevenueCatSub) {
        return privilege?.cardio_access ?? false;
      }
      return (privilege?.cardio_access && cardioSub?.stripe?.latest_invoice?.paid) ?? false;
    }
    return privilege?.cardio_access ?? false;
  }, [privilege.cardio_access, cardioSub?.stripe?.latest_invoice?.paid, user?.accountType]);

  const hasCardioPocAccess = useMemo(() => {
    return privilege?.cardio_poc_access ?? false;
  }, [privilege.cardio_poc_access]);

  return (
    <div className="container">
      {isLoading ? <div id="homepage-loading" data-testid="homepage-loading"></div> : <></>}
      <div className="main testselection">
        <Header />
        {anyAccess ? (
          <>
            <p className="mt-5 heading">Which test would you like to do?</p>
            <div className="buttons-div mt-5">
              {hasCovidAccess && (
                <div className="button">
                  <img src={CovidTest} alt="covid-test" onClick={() => handleCovidClick(false)} />
                  <span>COVID</span>
                </div>
              )}
              {hasCovidDemoAccess && (
                <div className="button">
                  <img src={CovidTest} alt="covid-test-demo" onClick={() => handleCovidClick(true)} />
                  <span>COVID Demo</span>
                </div>
              )}
              {hasCardioAccess && (
                <div className="button">
                  <img src={CardioIcon} alt="cardio" onClick={() => history.push("/cardio")} />
                  <span>Cardio</span>
                </div>
              )}
              {hasCardioPocAccess && (
                <div className="button">
                  <img src={CardioIcon} alt="cardio-poc" onClick={() => history.push("/cardio-poc")} />
                  <span>Cardio POC</span>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="mt-5 heading">No tests available</p>
          </>
        )}
      </div>
    </div>
  );
}
